import { CustomAxiosType } from "../types/types";
import { client } from "./client";

/** 빅데이터 분석 조회 조건. 범위(소속)는 서버가 로그인 계정 기준으로 다시 정한다 */
export interface AnalysisFilter {
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd (포함)
  groupId?: string; // "all" 또는 하위 소속 아이디
  vhclTypeCd?: number | null;
  keyword?: string; // 기기(차량) 명·소유자 명·단말번호 일부
}

type Num = number | string | null | undefined;

export interface AnalysisKpi {
  trips: Num;
  vehicles: Num;
  hours: Num;
  km: Num;
  open_trips: Num;
  odd_trips: Num;
  rollover: Num;
  danger: Num;
  idle: Num;
  leave_area: Num;
  theft: Num;
  enter_danger: Num;
  rollover_false: Num;
  rollover_119: Num;
  rollover_center: Num;
  rollover_pending: Num;
  danger_to_rollover: Num;
  rollover_handle_min: Num;
}

export interface AnalysisOverview {
  period: {
    start: string;
    end: string;
    prevStart: string;
    prevEnd: string;
    scope: string | null;
  };
  kpi: AnalysisKpi;
  prevKpi: AnalysisKpi;
  devices: { registered: Num; active7d: Num; active30d: Num };
  monthly: { ym: string; trips: Num; vehicles: Num; hours: Num; km: Num }[];
  monthlyEvents: { ym: string; rollover: Num; danger: Num; area: Num; etc: Num }[];
  lastYearMonthly: { ym: string; trips: Num; vehicles: Num; hours: Num; km: Num }[];
  lastYearMonthlyEvents: { ym: string; rollover: Num; danger: Num; area: Num; etc: Num }[];
  weekHour: { dw: number; hr: number; n: Num }[];
  weekHourEvents: { dw: number; hr: number; n: Num; rollover?: Num; danger?: Num }[];
  duration: { bucket: string; n: Num }[];
  byType: {
    type_cd: number | string;
    name: string;
    registered?: Num;
    trips?: Num;
    vehicles?: Num;
    hours?: Num;
    km?: Num;
    rollover?: Num;
    danger?: Num;
    rollover_false?: Num;
    rollover_119?: Num;
    danger_to_rollover?: Num;
  }[];
  vhclTypes: { code: number | string; name: string }[];
}

export interface AnalysisEventPoint {
  id: number;
  type: string; // 2 전복, 3 위험운전, 5 시동방치, 6 사용지역이탈, 7 도난감지, 9 위험지역진입
  dt: string;
  lat: number;
  lot: number;
  speed: Num;
  status: number;
  result: "pending" | "false" | "119" | "center" | "to_rollover" | "done";
  type_cd: number | null;
  vhcl_nm?: string | null;
  dvc_sn?: string | null;
  ogdp_nm?: string | null;
}

/** 이벤트 목록 분류 — 서버 매퍼 eventListWhere 와 같다 */
export type EventListCategory =
  | "119"
  | "center"
  | "false"
  | "other"
  | "pending"
  | "to_rollover"
  | "rollover"
  | "danger"
  | "rollover_danger"
  | "area"
  | "all";

/** 운행시간 구간 — 서버 매퍼 operListWhere 와 같다 (abnormal = 종료 없음·시각 이상·24시간 초과) */
export type DurBucket = "m10" | "m30" | "h1" | "h2" | "h4" | "h8" | "h24" | "over" | "odd" | "open" | "abnormal";

/** 상세 목록 조건. 화면에서 누른 항목으로 기간 안을 더 좁힌다 */
export interface ListQuery {
  category?: EventListCategory;
  month?: string; // yyyy-MM
  dw?: number; // 1(일) ~ 7(토), MariaDB DAYOFWEEK
  hr?: number; // 0 ~ 23
  durBucket?: DurBucket;
  /** 차종을 누른 경우 화면 차종 조건 대신 쓴다 */
  vhclTypeCd?: number | null;
}

/** 목록 응답: rows 는 최대 2000건, total 은 조건에 맞는 전체 건수 */
export interface ListResult<T> {
  rows: T[];
  total: number;
}

/** 운행 목록 한 줄 (tb_oper) */
export interface AnalysisTripRow {
  id: number;
  start_dt: string;
  end_dt: string | null;
  secs: Num;
  km: Num;
  dvc_sn: string | null;
  vhcl_nm: string | null;
  type_cd: number | null;
  ogdp_nm: string | null;
  /** ok, open 종료 없음, odd 시각 이상, over 24시간 초과 */
  flag: "ok" | "open" | "odd" | "over";
}

/** 차량별 운행·이벤트 집계 */
export interface AnalysisVehicleRow {
  vhcl_id: number | null;
  vhcl_nm: string | null;
  type_cd: number | null;
  dvc_sn: string | null;
  ogdp_nm: string | null;
  trips: Num;
  hours: Num;
  km: Num;
  last_trip: string | null;
  rollover: Num;
  danger: Num;
  area: Num;
  rollover_false: Num;
  /** 차량이 연결되지 않은 운행 묶음 (운행 대수에 들어가지 않는다) */
  unlinked?: boolean;
}

/** 단말 수신 목록 */
export interface AnalysisDeviceRow {
  vhcl_id: number;
  vhcl_nm: string | null;
  type_cd: number | null;
  dvc_sn: string | null;
  ogdp_nm: string | null;
  /** 마지막 수신 (30일 이내만) */
  last_rcv: string | null;
  active: "7d" | "30d" | "none";
}

/** 처리 결과별 이벤트 목록 한 줄 */
export interface AnalysisEventListRow {
  id: number;
  type: string;
  dt: string;
  dvc_sn: string | null;
  vhcl_nm: string | null;
  type_cd: number | null;
  ogdp_nm: string | null;
  speed: Num;
  status: number | null;
  rmv_rsn: string | null;
  rmv_dt: string | null;
  handle_min: Num;
  addr: string | null;
}

/** 원시 위치로그 한 줄 (tb_pstn_log) */
export interface AnalysisRawRow {
  t: string; // 로그 수집 일시
  reg: string | null; // 로그 등록 일시
  stts: number | null; // 0 위치, 1 시동ON, 2 시동OFF
  evt: number | null; // 0 정상, 1 시동ON, 2 시동OFF, 3 헬스체크, 11 전복사고
  lat: Num;
  lot: Num;
  speed: Num;
  heading: Num;
  fix: Num;
  sat: Num;
  hdop: Num;
  tiltx: Num;
  tilty: Num;
  tiltz: Num;
  gyrox: Num;
  gyroy: Num;
  gyroz: Num;
  vib: Num;
  vvolt: Num;
  bvolt: Num;
  cpu: Num;
  mvmn: Num;
  acml: Num;
  rsrp: Num;
}

export interface AnalysisEventRaw {
  event: {
    id: number;
    type: string;
    dt: string;
    dvc_sn: string | null;
    vhcl_id: number | null;
    vhcl_nm: string | null;
    type_cd: number | null;
    type_nm: string | null;
    ogdp_nm: string | null;
    lat: Num;
    lot: Num;
    speed: Num;
    status: number | null;
    rmv_rsn: string | null;
    rmv_dt: string | null;
    addr: string | null;
    opr_id: number | null;
  };
  beforeMin: number;
  afterMin: number;
  from: string | null;
  to: string | null;
  rows: AnalysisRawRow[];
}

export interface AnalysisRegion {
  adm_cd: string;
  adm_nm: string;
  sd_nm: string;
  sgg_nm: string;
  trips: number;
  hours: number;
  rollover: number;
  danger: number;
  area: number;
  etc: number;
}

export interface AnalysisRegions {
  regions: AnalysisRegion[];
  polygons: Record<string, [number, number][]>;
  unmatchedTrips: number;
  unmatchedEvents: number;
}

const body = (f: AnalysisFilter) => ({
  startDate: f.startDate,
  endDate: f.endDate,
  groupId: f.groupId ?? "all",
  vhclTypeCd: f.vhclTypeCd ?? null,
  keyword: f.keyword || null,
});

// 요약 KPI · 월별 · 요일×시간대 · 운행시간 분포 · 차종별
export const getAnalysisOverview = async (f: AnalysisFilter) => {
  const response = await client.post<CustomAxiosType<AnalysisOverview>>(
    `/api/analysis/overview/get`,
    body(f)
  );
  return response.data.result;
};

// 지도용 이벤트 지점 (최대 2만 건)
export const getAnalysisEvents = async (f: AnalysisFilter) => {
  const response = await client.post<CustomAxiosType<AnalysisEventPoint[]>>(
    `/api/analysis/events/get`,
    body(f)
  );
  return response.data.result ?? [];
};

const listBody = (f: AnalysisFilter, q: ListQuery) => ({
  ...body(f),
  ...(q.vhclTypeCd !== undefined ? { vhclTypeCd: q.vhclTypeCd } : {}),
  category: q.category ?? null,
  month: q.month ?? null,
  dw: q.dw ?? null,
  hr: q.hr ?? null,
  durBucket: q.durBucket ?? null,
});

const listOf = async <T,>(path: string, payload: object): Promise<ListResult<T>> => {
  const response = await client.post<CustomAxiosType<T[] | null>>(path, payload);
  const rows = response.data.result ?? [];
  return { rows, total: response.data.count ?? rows.length };
};

// 이벤트 목록 (최근 2000건)
export const getAnalysisEventList = (f: AnalysisFilter, q: ListQuery) =>
  listOf<AnalysisEventListRow>(`/api/analysis/events/list/get`, listBody(f, q));

// 운행 목록 (최근 2000건)
export const getAnalysisTripList = (f: AnalysisFilter, q: ListQuery) =>
  listOf<AnalysisTripRow>(`/api/analysis/trips/list/get`, listBody(f, q));

// 차량별 운행·이벤트 집계
export const getAnalysisVehicleList = (f: AnalysisFilter, q: ListQuery) =>
  listOf<AnalysisVehicleRow>(`/api/analysis/vehicles/list/get`, listBody(f, q));

// 단말 수신 목록 (등록 단말 전체)
export const getAnalysisDeviceList = (f: AnalysisFilter) =>
  listOf<AnalysisDeviceRow>(`/api/analysis/devices/list/get`, body(f));

// 이벤트 한 건의 차량 정보와 앞뒤 원시 위치로그
export const getAnalysisEventRaw = async (id: number, beforeMin: number, afterMin: number) => {
  const response = await client.post<CustomAxiosType<AnalysisEventRaw>>(
    `/api/analysis/event/raw/get`,
    { id, beforeMin, afterMin }
  );
  return response.data.result;
};

// 행정동별 집계 + 단순화한 경계
export const getAnalysisRegions = async (f: AnalysisFilter) => {
  const response = await client.post<CustomAxiosType<AnalysisRegions>>(
    `/api/analysis/regions/get`,
    body(f)
  );
  return response.data.result;
};
